/**
 * What the user has selected, as a value.
 *
 * A selection names events by id and nothing else. It holds no positions, no rectangles and
 * no reference to the drawn page, which is what lets it survive an edit: inserting a bar
 * before the selection, re-rendering at another zoom or reflowing the systems all leave it
 * meaning exactly what it meant. A selection that stored coordinates would have to be
 * rebuilt after every one of those, and would be silently wrong in between.
 *
 * Every function here returns a new selection rather than changing one, for the same reason
 * the document is immutable: undo, and the ability to compare the selection before and
 * after an edit without having kept a copy.
 *
 * The score is passed in wherever an answer depends on it. Nothing here caches a score or
 * holds a reference to one, so a selection made against one document can be asked about
 * another -- which is what happens on every edit, since an edit produces a new document.
 */

import type { EventId, MeasureId, PartId } from "../document/ids";
import { capacityOf, eventOnsets, locateEvent } from "../document/query";
import type { EventLocation } from "../document/query";
import type { NoteEvent, Score } from "../document/types";

/**
 * A set of selected events, with the two ends a shift-click extends between.
 *
 * `anchor` is where the current range began and `focus` is the event last touched; keeping
 * both is what makes extending a selection behave the way it does everywhere else -- the
 * anchor stays put while the focus moves, so shrinking a range back towards its start works
 * without a second gesture.
 */
export interface Selection {
  readonly events: ReadonlySet<EventId>;
  readonly anchor: EventId | null;
  readonly focus: EventId | null;
  /** Which note of the focus event's chord the user is on; `0` for a single note or rest. */
  readonly pitchIndex: number;
}

export const NOTHING: Selection = {
  events: new Set<EventId>(),
  anchor: null,
  focus: null,
  pitchIndex: 0,
};

export function isEmpty(selection: Selection): boolean {
  return selection.events.size === 0;
}

export function isSelected(selection: Selection, id: EventId): boolean {
  return selection.events.has(id);
}

/** Replace the selection with one event: an ordinary click. */
export function selectOne(id: EventId, pitchIndex = 0): Selection {
  return { events: new Set([id]), anchor: id, focus: id, pitchIndex };
}

/**
 * Add an event to the selection, or take it out again: a modifier-click.
 *
 * Removing the focus leaves the anchor where it was rather than clearing it, so a range can
 * still be extended from the same place after a stray tone has been deselected.
 */
export function toggle(selection: Selection, id: EventId, pitchIndex = 0): Selection {
  const events = new Set(selection.events);
  if (events.delete(id)) {
    return { events, anchor: selection.anchor, focus: selection.focus, pitchIndex };
  }
  events.add(id);
  return { events, anchor: selection.anchor ?? id, focus: id, pitchIndex };
}

/** Move within a chord without changing which events are selected. */
export function selectPitch(selection: Selection, pitchIndex: number): Selection {
  return { ...selection, pitchIndex: Math.max(0, pitchIndex) };
}

/** The event the keyboard is on, located in the score, or `null`. */
export function focusLocation(score: Score, selection: Selection): EventLocation | null {
  return selection.focus === null ? null : locateEvent(score, selection.focus);
}

/** Ticks from the start of the piece to each bar, computed once for a whole walk. */
function measureOnsets(score: Score): readonly number[] {
  const onsets: number[] = [];
  let onset = 0;
  for (let index = 0; index < score.measures.length; index += 1) {
    onsets.push(onset);
    onset += capacityOf(score, index);
  }
  return onsets;
}

/** One event of the score, with everything needed to decide whether a range covers it. */
interface Placed {
  readonly id: EventId;
  readonly partIndex: number;
  readonly voiceIndex: number;
  readonly onset: number;
}

/** Every event of the score in reading order, with its place in the piece. */
function placedEvents(score: Score): readonly Placed[] {
  const onsets = measureOnsets(score);
  const placed: Placed[] = [];
  score.parts.forEach((part, partIndex) => {
    score.measures.forEach((global, measureIndex) => {
      const measure = part.measures[global.id];
      if (measure === undefined) return;
      const base = onsets[measureIndex] ?? 0;
      measure.voices.forEach((voice, voiceIndex) => {
        const within = eventOnsets(voice);
        voice.events.forEach((event, eventIndex) => {
          placed.push({
            id: event.id,
            partIndex,
            voiceIndex,
            onset: base + (within[eventIndex] ?? 0),
          });
        });
      });
    });
  });
  return placed;
}

/**
 * Extend the selection from its anchor to another event.
 *
 * The range is a rectangle in the two dimensions a score actually has: time across, parts
 * down. Everything that starts between the two events, in every part between them, comes in
 * -- which is what a musician means by "from here to there", and what makes it possible to
 * select a passage of the whole orchestra by clicking its first and last note.
 *
 * With no anchor to extend from, this is an ordinary click.
 */
export function extend(score: Score, selection: Selection, to: EventId, pitchIndex = 0): Selection {
  const anchor = selection.anchor;
  if (anchor === null || anchor === to) return selectOne(to, pitchIndex);

  const placed = placedEvents(score);
  const from = placed.find((item) => item.id === anchor);
  const target = placed.find((item) => item.id === to);
  if (from === undefined || target === undefined) return selectOne(to, pitchIndex);

  const lowPart = Math.min(from.partIndex, target.partIndex);
  const highPart = Math.max(from.partIndex, target.partIndex);
  const lowOnset = Math.min(from.onset, target.onset);
  const highOnset = Math.max(from.onset, target.onset);

  const events = new Set<EventId>();
  for (const item of placed) {
    if (item.partIndex < lowPart || item.partIndex > highPart) continue;
    if (item.onset < lowOnset || item.onset > highOnset) continue;
    events.add(item.id);
  }
  return { events, anchor, focus: to, pitchIndex };
}

/** Select every event of one bar of one part, across all of its voices. */
export function selectMeasure(score: Score, partId: PartId, measureId: MeasureId): Selection {
  const part = score.parts.find((candidate) => candidate.id === partId);
  const measure = part?.measures[measureId];
  if (measure === undefined) return NOTHING;

  const events = new Set<EventId>();
  let first: EventId | null = null;
  let last: EventId | null = null;
  for (const voice of measure.voices) {
    for (const event of voice.events) {
      events.add(event.id);
      first ??= event.id;
      last = event.id;
    }
  }
  return { events, anchor: first, focus: last, pitchIndex: 0 };
}

/**
 * Everything in the score, or everything in one part when one is named.
 *
 * Selecting the whole piece is the one selection that cannot be made by clicking, and it is
 * what "apply this to all of it" means: transpose the score, clear every mark, delete the
 * lot. The anchor and focus are the first and last events in reading order, so extending
 * from it afterwards behaves like any other selection.
 */
export function selectAll(score: Score, partId: PartId | null = null): Selection {
  const events = new Set<EventId>();
  let first: EventId | null = null;
  let last: EventId | null = null;

  for (const part of score.parts) {
    if (partId !== null && part.id !== partId) continue;
    for (const global of score.measures) {
      const measure = part.measures[global.id];
      if (measure === undefined) continue;
      for (const voice of measure.voices) {
        for (const event of voice.events) {
          events.add(event.id);
          first ??= event.id;
          last = event.id;
        }
      }
    }
  }
  return { events, anchor: first, focus: last, pitchIndex: 0 };
}

/**
 * Move the focus by whole events, forwards or backwards.
 *
 * Movement stays in the part and voice the focus is in, and crosses bar lines, because that
 * is the line the arrow keys are following: the musical line, not the page. Reaching either
 * end of it leaves the selection where it is rather than wrapping, since wrapping from the
 * last bar to the first is never what was meant.
 */
export function step(score: Score, selection: Selection, delta: number): Selection {
  const focus = selection.focus;
  if (focus === null || delta === 0) return selection;

  const line = placedEvents(score).filter((item) => sameLine(score, item, focus));
  const at = line.findIndex((item) => item.id === focus);
  if (at === -1) return selection;
  const next = line[at + delta];
  return next === undefined ? selection : selectOne(next.id);
}

function sameLine(score: Score, item: Placed, focus: EventId): boolean {
  const here = locateEvent(score, focus);
  if (here === null) return false;
  const partIndex = score.parts.findIndex((part) => part.id === here.partId);
  return item.partIndex === partIndex && item.voiceIndex === here.voiceIndex;
}

/** Every selected event, located, in reading order; ids the score no longer holds are left out. */
export function selectedLocations(score: Score, selection: Selection): readonly EventLocation[] {
  const found: EventLocation[] = [];
  for (const item of placedEvents(score)) {
    if (!selection.events.has(item.id)) continue;
    const location = locateEvent(score, item.id);
    if (location !== null) found.push(location);
  }
  return found;
}

/** A located event that is known to be a note rather than a rest. */
export interface NoteLocation extends EventLocation {
  readonly event: NoteEvent;
}

/**
 * The selected notes, in reading order.
 *
 * Most marks -- articulations, ornaments, stems, beams, ties -- belong to notes and have no
 * meaning on a rest, so the layers that write them start here rather than filtering for
 * themselves. Fermatas and cues work from {@link selectedLocations} instead: a silence can
 * be held, and a quoted passage is finished with small rests.
 */
export function selectedNotes(score: Score, selection: Selection): readonly NoteLocation[] {
  return selectedLocations(score, selection).filter(
    (location): location is NoteLocation => location.event.kind === "note",
  );
}

/**
 * Drop from a selection everything a score no longer contains.
 *
 * Called after an edit that removed events. A selection is not corrupted by pointing at a
 * deleted note -- every function here ignores what it cannot find -- but leaving it that way
 * would let the user delete twice and act on a phantom, so an editor prunes after each edit.
 */
export function prune(score: Score, selection: Selection): Selection {
  const live = new Set<EventId>();
  for (const item of placedEvents(score)) {
    if (selection.events.has(item.id)) live.add(item.id);
  }
  if (live.size === selection.events.size) return selection;
  const keep = (id: EventId | null): EventId | null => (id !== null && live.has(id) ? id : null);
  return {
    events: live,
    anchor: keep(selection.anchor),
    focus: keep(selection.focus),
    pitchIndex: selection.pitchIndex,
  };
}
